import { useEffect, useRef, useState } from 'react';
import { fetchRouletteVideos } from '../lib/sanity';

import './TextRotation.css';

function TextRotation() {
  const textContainer1Ref = useRef(null);
  const textContainer2Ref = useRef(null);
  const textContainer3Ref = useRef(null);
  const longContainerRef = useRef(null);
  const videoRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const [rouletteVideos, setRouletteVideos] = useState(null);
  const [currentVideo, setCurrentVideo] = useState('webDesignVideoUrl');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    // Fetch roulette videos from CMS
    const loadRouletteVideos = async () => {
      try {
        const videos = await fetchRouletteVideos();
        if (videos) {
          setRouletteVideos(videos);
          setIsActive(true);
          // Set initial video
          setCurrentVideo('webDesignVideoUrl');
        } else {
          // No active roulette videos found, hide the module
          setIsActive(false);
        }
      } catch (error) {
        console.error('Failed to load roulette videos:', error);
        setIsActive(false);
      }
    };

    loadRouletteVideos();
  }, []);

  useEffect(() => {
    // If no active roulette videos, don't set up scroll handlers
    if (!isActive) return;

    const getScrollPoints = () => {
      if (!longContainerRef.current) return null;
      
      const containerTop = longContainerRef.current.offsetTop;
      const viewportHeight = window.innerHeight;
      return {
        start: containerTop,
        firstRotation: containerTop + viewportHeight * 0.005,
        secondRotation: containerTop + viewportHeight * 1.25,
        end: containerTop + viewportHeight * 20
      };
    };

    const handleScroll = () => {
      if (isAnimatingRef.current || !longContainerRef.current) return;

      const scrollPoints = getScrollPoints();
      if (!scrollPoints) return;

      const currentScroll = window.scrollY;
      let rotation1 = 0;
      let rotation2 = 0;
      let rotation3 = 0;
      let newVideo = currentVideo;

      if (currentScroll < scrollPoints.start) {
        rotation1 = 0;
        rotation2 = -90;
        rotation3 = -90;
        newVideo = 'webDesignVideoUrl';
      } else if (currentScroll < scrollPoints.firstRotation) {
        rotation1 = 0;
        rotation2 = -90;
        rotation3 = -90;
        newVideo = 'webDesignVideoUrl';
      } else if (currentScroll < scrollPoints.secondRotation) {
        rotation1 = 90;
        rotation2 = 0;
        rotation3 = -90;
        newVideo = 'motionDesignVideoUrl';
      } else if (currentScroll < scrollPoints.end) {
        rotation1 = 180;
        rotation2 = 90;
        rotation3 = 0;
        newVideo = 'threeDDesignVideoUrl';
      }

      // Update video if it changed
      if (newVideo !== currentVideo && rouletteVideos && videoRef.current) {
        setCurrentVideo(newVideo);
        const videoElement = videoRef.current;
        videoElement.src = rouletteVideos[newVideo];
        videoElement.load();
        videoElement.play().catch(() => {
          // Video autoplay failed
        });
      }

      isAnimatingRef.current = true;
      
      if (textContainer1Ref.current) {
        textContainer1Ref.current.style.transform = `rotateX(${rotation1}deg)`;
      }
      if (textContainer2Ref.current) {
        textContainer2Ref.current.style.transform = `rotateX(${rotation2}deg)`;
      }
      if (textContainer3Ref.current) {
        textContainer3Ref.current.style.transform = `rotateX(${rotation3}deg)`;
      }
      
      setTimeout(() => {
        isAnimatingRef.current = false;
      }, 50);
    };

    const handleResize = () => {
      // Force recalculation of scroll points
      window.dispatchEvent(new Event('scroll'));
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    // Initial scroll trigger
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [currentVideo, rouletteVideos, isActive]);

  // If no active roulette videos, don't render anything
  if (!isActive) {
    return null;
  }

  // Get the current video URL
  const getCurrentVideoUrl = () => {
    if (!rouletteVideos) return '/assets/showreelpreview.mp4'; // Fallback
    return rouletteVideos[currentVideo] || '/assets/showreelpreview.mp4';
  };

  return (
    <div ref={longContainerRef} className="long-container">
      <div className="sticky-div">
        <div className="video-container">
          <video 
            ref={videoRef}
            autoPlay 
            loop 
            muted 
            playsInline
            key={getCurrentVideoUrl()} // Force re-render when video changes
          >
            <source src={getCurrentVideoUrl()} type="video/mp4" />
          </video>
        </div>
        <div ref={textContainer1Ref} className="text-container-1">
          <h2 className="rotation-title title-1">Web<br/>Design</h2>
        </div>
        <div ref={textContainer2Ref} className="text-container-2">
          <h2 className="rotation-title title-2">Motion<br/>Design</h2>
        </div>
        <div ref={textContainer3Ref} className="text-container-3">
          <h2 className="rotation-title title-3">3D<br/>Design</h2>
        </div>
      </div>
    </div>
  );
}

export default TextRotation; 